import { defineStore } from 'pinia'
import { ref } from 'vue'
// @ts-expect-error: vuex
import { useStore } from 'vuex'
import childDocumentService from '@/services/http/api/childDocument'
import childEditionService from '@/services/http/api/childEdition'
import isString from 'lodash.isstring'

import { useChildDocumentStore } from '@/stores'
import { useGuardianConsentStore } from '@/stores'
import { useCoordinatorQuestionnaireStore } from '@/stores'

import { shouldRetryGetActionForAssignedEndpoint } from '@/helpers/retryActionForOtherEndpoint'
import type { TODO } from '@/types'
import { useChildEditionFlowActionsStore } from '@/stores/childEditionFlowActions'

export const usePublishStore = defineStore('publish', () => {
  const childDocumentStore = useChildDocumentStore()
  const guardianConsentStore = useGuardianConsentStore()
  const coordinatorQuestionnaireStore = useCoordinatorQuestionnaireStore()
  const childEditionFlowActionsStore = useChildEditionFlowActionsStore()

  const vuexStore = useStore()

  const loader = ref(false)
  const publishLoader = ref(false)

  const clearDocuments = () => {
    childDocumentStore.clear()
    guardianConsentStore.clearGuardianConsent()
    coordinatorQuestionnaireStore.clearCoordinatorQuestionnaire()
  }

  const fetchSummary = async (payload: TODO) => {
    try {
      const { data } = await childDocumentService.getByType(payload, payload.getAssignedFirst)
      if (!data?.data || typeof data?.data !== 'object') return
      Object.entries(data.data).forEach(([key]) => {
        const documentSetterMap = childDocumentStore.documentSetterMap
        const documentSetter = documentSetterMap[key as keyof typeof documentSetterMap]
        const documentData = data.data?.[key]
        documentSetter(documentData)
      })
    } catch (error) {
      if (shouldRetryGetActionForAssignedEndpoint(error, payload)) {
        await fetchSummary({ ...payload, getAssignedFirst: true })
      }
    }
  }

  const init = async (payload: TODO) => {
    loader.value = true
    clearDocuments()

    let editionName
    try {
      editionName = await vuexStore.dispatch(
        'childEdition/getChildsEditionName',
        { childEditionId: payload?.childEditionId },
        { root: true }
      )
    } catch (error) {
      return Promise.reject(error)
    }
    if (!isString(editionName) || editionName.length === 0) return null

    try {
      await childDocumentStore.fetchResource({ documentType: 'guardian-consent', editionName })
      await childDocumentStore.fetchResource({
        documentType: 'coordinator-questionnaire',
        editionName
      })
      await fetchSummary(payload)
    } finally {
      loader.value = false
    }
  }

  const publish = async (childEditionId: string) => {
    publishLoader.value = true
    try {
      const { data } = await childEditionService.publish(childEditionId)
      const { school_hydra_users: schoolHydraUsers } =
        vuexStore.getters['childEdition/getChild'] ?? {}
      vuexStore.commit(
        'childEdition/setChild',
        {
          child_edition: data?.data?.child_edition,
          school_hydra_users: schoolHydraUsers
        },
        { root: true }
      )
      vuexStore.commit('childEdition/setChildEditionRequestSkipped', true, { root: true })

      childEditionFlowActionsStore.updateChildEditionFlowActionsFromBackendResponse(data)
    } finally {
      clearDocuments()
      publishLoader.value = false
    }
  }

  return {
    // state
    loader,
    publishLoader,
    // actions
    clearDocuments,
    fetchSummary,
    init,
    publish
  }
})
